// src/services/nativeBridge.ts
/**
 * Task player as shown in the app's system notification. Mirrors
 * TaskPlayerState in the app (daex-app modules/task-player). Times are epoch
 * ms, so the phone runs the clock itself.
 */
export type NativeTaskPlayerState = {
  mode: "work" | "break" | "idle";
  title: string;
  subtitle?: string;
  // Clock counts up from startedAt, or down to endsAt (Pomodoro)
  startedAt?: number;
  endsAt?: number;
  // 0..1 now, and how much it grows per minute while running
  progress?: number;
  progressPerMinute?: number;
  // Alerting notification when the clock reaches endsAt
  alert?: { title: string; body?: string };
};

/** A calendar on the phone (iCloud, local, Google/Exchange accounts, ...) */
export type DeviceCalendar = {
  id: string;
  title: string;
  color?: string;
  sourceName: string;
  selected: boolean;
};

export type DeviceCalendarStatus = {
  permission: "granted" | "denied" | "undetermined";
  // false: the user has to allow it in the system settings
  canAskAgain: boolean;
  // Shown in DAEX at all (independent of the system permission)
  connected: boolean;
  calendars: DeviceCalendar[];
};

// Requests the app answers with a RESPONSE carrying the same requestId
export type NativeRequest =
  | { type: "DEVICE_CALENDAR_STATUS" }
  // Asks for the permission; preselects all calendars except those of the
  // given providers (already connected via OAuth, would show up twice)
  | { type: "DEVICE_CALENDAR_CONNECT"; connectedProviders: string[] }
  | { type: "DEVICE_CALENDAR_SELECT"; calendarIds: string[] }
  | { type: "DEVICE_CALENDAR_DISCONNECT" }
  | { type: "DEVICE_CALENDAR_EVENTS"; timeMin: string; timeMax: string };

export type NativeOutMessage =
  | (NativeRequest & { requestId: string })
  // Opens the system's own preview of a device calendar event
  | { type: "DEVICE_CALENDAR_OPEN"; id: string }
  | { type: "OPEN_PUSH_SETTINGS" }
  | { type: "OAUTH_OPEN"; url: string }
  // null: nothing to show
  | { type: "TASK_PLAYER_STATE"; state: NativeTaskPlayerState | null };

export type NativeInMessage = {
  type: "RESPONSE";
  requestId: string;
  ok: boolean;
  data?: unknown;
  reason?: string;
};

function getRNBridge(): any {
  // @ts-ignore
  return typeof window !== "undefined"
    ? (window as any).ReactNativeWebView
    : undefined;
}

export function isNativeApp(): boolean {
  return (
    typeof window !== "undefined" && (window as any).__DAEX_NATIVE__ === true
  );
}

// Providers the app can run in the system browser (unset in older app versions)
export function supportsNativeOAuth(provider: string): boolean {
  if (typeof window === "undefined") return false;
  const providers = (window as any).__DAEX_NATIVE_OAUTH__;
  return Array.isArray(providers) && providers.includes(provider);
}

// Features of the app version this runs in (unset in older app versions)
export function supportsNativeFeature(feature: "deviceCalendar"): boolean {
  if (typeof window === "undefined") return false;
  const features = (window as any).__DAEX_NATIVE_FEATURES__;
  return Array.isArray(features) && features.includes(feature);
}

export function sendToNative(msg: NativeOutMessage): boolean {
  const bridge = getRNBridge();
  if (!bridge?.postMessage) return false;
  bridge.postMessage(JSON.stringify(msg));
  return true;
}

export function openNativeMenu(): boolean {
  const bridge = (window as any).DAEX_NATIVE_BRIDGE;

  if (!bridge?.postMessage) return false;

  bridge.postMessage(JSON.stringify({ type: "OPEN_NATIVE_MENU" }));
  return true;
}

export function onNativeMessage(handler: (msg: NativeInMessage) => void) {
  const listener = (event: MessageEvent) => {
    try {
      const data =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      if (data?.type) handler(data as NativeInMessage);
    } catch {}
  };

  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}

// No crypto.randomUUID: it is missing outside secure contexts (dev server via LAN IP)
let requestCounter = 0;

/** Sends a request to the app and resolves with the data of its answer */
export function requestNative<T>(
  request: NativeRequest,
  timeoutMs = 10000,
): Promise<T> {
  const requestId = `${Date.now()}-${++requestCounter}`;
  return new Promise<T>((resolve, reject) => {
    const off = onNativeMessage((msg) => {
      if (msg.type !== "RESPONSE" || msg.requestId !== requestId) return;
      off();
      clearTimeout(timer);
      if (msg.ok) resolve(msg.data as T);
      else reject(new Error(msg.reason || "failed"));
    });
    // Older app versions don't answer at all
    const timer = setTimeout(() => {
      off();
      reject(new Error("timeout"));
    }, timeoutMs);
    if (!sendToNative({ ...request, requestId })) {
      off();
      clearTimeout(timer);
      reject(new Error("no-bridge"));
    }
  });
}

/** Device events have no web link: the app shows the system's own preview */
export function openDeviceCalendarEvent(id: string): boolean {
  return sendToNative({ type: "DEVICE_CALENDAR_OPEN", id });
}
